import ConfirmDialog from "./ConfirmDialog"

import { PropsWithChildren, useState } from "react"
import { useTranslation } from "react-i18next"
import { ConfirmDialogContext } from "./ConfirmDialogContext"
import { Dialog, OpenDialogParams, State } from "./types"

type Translations = typeof defaultTranslations

const ConfirmDialogProvider = (props: PropsWithChildren<{}>) => {
  const translations = useTranslations()
  const [state, setState] = useState<State>({
    dialog: null,
    contextValue: {
      openDialog,
    },
  })

  function closeDialog() {
    setState((state) => {
      const newState = {
        ...state,
        dialog: null,
      }
      return newState
    })
  }

  function openDialog(
    {
      onConfirm,
      onCancel = () => {},
      primaryActionButtonLabel = translations.primaryActionButtonLabel,
      secondaryActionButtonLabel = translations.secondaryActionButtonLabel,
      text = translations.text,
      title = translations.title,
    }: OpenDialogParams,
    ...args: [...any[]]
  ) {
    const handleCancel = async () => {
      closeDialog()
      await onCancel(...args)
    }

    const handleConfirm = async () => {
      closeDialog()
      await onConfirm(...args)
    }

    return setState((state) => {
      const dialog: Dialog = {
        handleCancel,
        handleClose: closeDialog,
        handleConfirm,
        open: true,
        primaryActionButtonLabel,
        secondaryActionButtonLabel,
        title,
        text,
      }

      return {
        ...state,
        dialog,
      }
    })
  }

  return (
    <ConfirmDialogContext.Provider value={state.contextValue}>
      {props.children}
      {state.dialog && <ConfirmDialog dialog={state.dialog} />}
    </ConfirmDialogContext.Provider>
  )
}

const defaultTranslations = {
  primaryActionButtonLabel: "Confirm",
  secondaryActionButtonLabel: "Cancel",
  title: "Confirm",
  text: "Are you sure you want to perform this action?",
}

const useTranslations = (): Translations => {
  const { t } = useTranslation()

  return {
    primaryActionButtonLabel: t("confirmationDialog.primaryActionButtonLabel", {
      defaultValue: defaultTranslations.primaryActionButtonLabel,
    }),
    secondaryActionButtonLabel: t("confirmationDialog.secondaryActionButtonLabel", {
      defaultValue: defaultTranslations.secondaryActionButtonLabel,
    }),
    title: t("confirmationDialog.title", { defaultValue: defaultTranslations.title }),
    text: t("confirmationDialog.text", { defaultValue: defaultTranslations.text }),
  }
}

export default ConfirmDialogProvider
